import { formatDate } from 'i18n/format';
import { t as translate } from 'i18n';
import { formatAmount, formatDelta, unitOf } from './units';

/**
 * Pure rows → trend card. Every number comes from the query's rows. The saved
 * chart says whether its measure may be summed; a bucket the period does not
 * fully cover is flagged and never compared. Dates are read as written, in
 * UTC, so the viewer's timezone cannot move a period.
 */
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}))?/;
// A wider card stops being readable and the table says more.
const MAX_BUCKETS = 400;
const DELTA_LABELS = {
  hour: 'assistant.trendCardToPreviousHour',
  day: 'assistant.trendCardToPreviousDay',
  week: 'assistant.trendCardToPreviousWeek',
  month: 'assistant.trendCardToPreviousMonth',
  quarter: 'assistant.trendCardToPreviousQuarter',
  year: 'assistant.trendCardToPreviousYear',
};
/** A change from `previous` to `current`: points for a rate, percent otherwise. */
function change(current, previous, column, chart, label) {
  const percent = unitOf(column) === 'percent';
  if (!percent && previous === 0) return null;
  const value = percent ? current - previous : ((current - previous) / Math.abs(previous)) * 100;
  return {
    value,
    text: formatDelta(value, percent ? translate('assistant.trendCardPoints') : '%'),
    label,
    good: value === 0 || (value > 0) === (chart.good_when !== 'down'),
  };
}

const GRAIN_LABELS = {
  hour: 'assistant.trendCardByHour',
  day: 'assistant.trendCardByDay',
  week: 'assistant.trendCardByWeek',
  month: 'assistant.trendCardByMonth',
  quarter: 'assistant.trendCardByQuarter',
  year: 'assistant.trendCardByYear',
};

const number = (value) => typeof value === 'number' && Number.isFinite(value);

function parse(value) {
  const match = typeof value === 'string' ? ISO.exec(value) : null;
  if (!match) return null;
  const [, year, month, day, hour] = match;
  const time = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour || 0));
  return Number.isFinite(time) ? new Date(time) : null;
}

function nextStart(date, grain) {
  const [year, month] = [date.getUTCFullYear(), date.getUTCMonth()];
  if (grain === 'hour') return new Date(date.getTime() + HOUR_MS);
  if (grain === 'day') return new Date(date.getTime() + DAY_MS);
  if (grain === 'week') return new Date(date.getTime() + 7 * DAY_MS);
  if (grain === 'month') return new Date(Date.UTC(year, month + 1, 1));
  if (grain === 'quarter') return new Date(Date.UTC(year, month + 3, 1));
  return new Date(Date.UTC(year + 1, 0, 1));
}

const shortDate = (date, options) => formatDate(date, { timeZone: 'UTC', ...options });

function labels(start, end, grain) {
  if (grain === 'hour') {
    const tick = shortDate(start, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    return { tick, name: `${shortDate(start, { day: 'numeric', month: 'long' })}, ${tick}` };
  }
  if (grain === 'day') return { tick: shortDate(start, { day: 'numeric', month: 'short' }), name: shortDate(start, { day: 'numeric', month: 'long', year: 'numeric' }) };
  if (grain === 'week') return {
    tick: shortDate(start, { day: 'numeric', month: 'short' }),
    name: `${shortDate(start, { day: 'numeric', month: 'short' })} – ${shortDate(new Date(end.getTime() - DAY_MS), { day: 'numeric', month: 'short', year: 'numeric' })}`,
  };
  if (grain === 'month') return { tick: shortDate(start, { month: 'short' }), name: shortDate(start, { month: 'long', year: 'numeric' }) };
  if (grain === 'quarter') {
    const name = translate('assistant.trendCardQuarter', { value1: Math.floor(start.getUTCMonth() / 3) + 1 });
    return { tick: name, name: `${name} ${start.getUTCFullYear()}` };
  }
  const year = String(start.getUTCFullYear());
  return { tick: year, name: year };
}

/** A time value's short axis label, as the cards write it; null when not a date. */
export function timeTick(value, grain = 'day') {
  const start = parse(value);
  return start && DELTA_LABELS[grain] ? labels(start, nextStart(start, grain), grain).tick : null;
}

/** The clock in the query's timezone, written the way Cube writes its buckets. */
function wallClock(today, timezone) {
  if (!timezone) return today;
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: timezone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit' })
      .formatToParts(today).map(({ type, value }) => [type, Number(value)]));
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour));
  } catch (error) {
    return today;
  }
}

/** The start of the bucket running now: any period ending after it is unfinished. */
function runningNow(today, grain, timezone) {
  const clock = wallClock(today, timezone);
  return new Date(Date.UTC(clock.getUTCFullYear(), clock.getUTCMonth(), clock.getUTCDate(), grain === 'hour' ? clock.getUTCHours() : 0));
}

const partialBucket = (start, end, period, now) => end > now || Boolean(period && (start < period[0] || end > period[1]));

/** Whether a time value's bucket is not yet, or not wholly, in the result. */
export function isPartial(value, grain, meta = {}, today = new Date()) {
  const start = parse(value);
  if (!start || !DELTA_LABELS[grain]) return false;
  return partialBucket(start, nextStart(start, grain), periodOf(meta.period), runningNow(today, grain, meta.timezone));
}

/** The period's own bounds, or null: `[start, end)` over whole days. */
function periodOf(period) {
  if (!Array.isArray(period) || period.length !== 2) return null;
  const [from, to] = period.map(parse);
  return from && to ? [from, new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() + 1))] : null;
}

/** Sorted buckets with missing periods as gaps: a gap is never an invented zero. */
function buckets(rows, xKey, yKey, grain, period, now) {
  const values = new Map();
  for (const row of rows) {
    const start = parse(row[xKey]);
    if (!start) return null;
    const key = start.getTime();
    // Two rows on one x mean another grouping, and either would hide the other.
    if (values.has(key)) return null;
    values.set(key, number(row[yKey]) ? row[yKey] : null);
  }
  if (!values.size) return null;
  const times = [...values.keys()].sort((a, b) => a - b);
  const result = [];
  let matched = 0;
  let start = new Date(times[0]);
  const last = times[times.length - 1];
  while (start.getTime() <= last) {
    if (result.length >= MAX_BUCKETS) return null;
    const end = nextStart(start, grain);
    const key = start.getTime();
    if (values.has(key)) matched += 1;
    const partial = partialBucket(start, end, period, now);
    result.push({ value: values.has(key) ? values.get(key) : null, partial, year: start.getUTCFullYear(), ...labels(start, end, grain) });
    start = end;
  }
  // Rows off this calendar (another week start) cannot share its buckets.
  return matched === times.length ? result : null;
}

function comparison(items, column, grain, chart) {
  if (chart.delta === 'none') return null;
  const whole = items.map((item, index) => ({ item, index })).filter(({ item }) => !item.partial && item.value !== null);
  const current = whole.at(-1);
  const previous = whole.at(-2);
  if (!current || !previous || previous.index !== current.index - 1) return null;
  return change(current.item.value, previous.item.value, column, chart, translate(DELTA_LABELS[grain]));
}

/** A kpi's change against its comparison query, when that query answered. */
export function kpiDelta(chart, columns, rows, meta = {}) {
  const key = chart.y?.[0];
  const column = columns.find((item) => item.key === key);
  const previous = meta.companions?.compare?.[key];
  if (chart.y?.length !== 1 || rows.length !== 1 || !column || !number(rows[0][key]) || !number(previous)) return null;
  return change(rows[0][key], previous, column, chart, translate('assistant.trendCardToCompared'));
}

function pick(items, highlight) {
  const known = items.map((item, index) => ({ value: item.value, partial: item.partial, index })).filter(({ value }) => value !== null);
  if (!known.length) return Math.max(0, items.length - 1);
  const whole = known.filter((item) => !item.partial);
  const pool = whole.length ? whole : known;
  if (highlight === 'max') return pool.reduce((best, item) => (item.value > best.value ? item : best)).index;
  if (highlight === 'min') return pool.reduce((best, item) => (item.value < best.value ? item : best)).index;
  return pool.at(-1).index;
}

/**
 * {buckets, selected, value, caption, delta, average, partial, unit} or
 * {fallback} when the rows cannot be drawn faithfully as one measure over time.
 * `meta` is the result's own `{period, truncated, timezone}`, which a filter
 * can change. The average skips unfinished periods and a cut result, and counts
 * only periods that have data.
 */
export function trendCard(chart, columns, rows, meta = {}, today = new Date()) {
  const refused = { fallback: 'shape' };
  const xColumn = columns.find((column) => column.key === chart.x);
  const yColumn = columns.find((column) => column.key === chart.y?.[0]);
  const grain = xColumn?.granularity;
  if (!xColumn || !yColumn || !DELTA_LABELS[grain]) return refused;
  const items = buckets(rows, chart.x, yColumn.key, grain, periodOf(meta.period), runningNow(today, grain, meta.timezone));
  if (!items) return refused;
  const known = items.filter((item) => item.value !== null);
  if (!known.length) return refused;
  const whole = known.filter((item) => !item.partial);
  const exact = chart.value === 'total' ? meta.companions?.total?.[yColumn.key] : undefined;
  const summed = (chart.value === 'sum' && !meta.truncated) || number(exact);
  const shown = whole.at(-1) || known.at(-1);
  const value = number(exact) ? exact : summed ? known.reduce((sum, item) => sum + item.value, 0) : shown.value;
  // A span across years must name both: the total covers all of it.
  const from = items[0].year === items.at(-1).year ? items[0].tick : items[0].name;
  const span = items.length > 1 ? `${from} – ${items.at(-1).name}` : items[0].name;
  // An exact total covers its query's period, which cut rows may not reach.
  const bounds = number(exact) ? periodOf(meta.period) : null;
  const totalSpan = bounds ? `${labels(bounds[0], bounds[1], 'day').tick} – ${labels(new Date(bounds[1].getTime() - DAY_MS), bounds[1], 'day').name}` : span;
  return {
    unit: yColumn,
    buckets: items,
    selected: pick(items, chart.highlight),
    value: formatAmount(value, yColumn),
    caption: number(exact) && !bounds && meta.truncated ? translate('assistant.trendCardWholePeriod') : summed ? `${totalSpan}, ${translate(GRAIN_LABELS[grain])}` : shown.name,
    delta: comparison(items, yColumn, grain, chart),
    average: chart.average && !meta.truncated && whole.length > 1 ? whole.reduce((sum, item) => sum + item.value, 0) / whole.length : null,
    partial: items.some((item) => item.partial),
    target: number(chart.target) ? { value: chart.target, label: chart.target_label || translate('assistant.trendCardTarget') } : null,
  };
}
